import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import Landmark from '@core/scripts/components/landmark-adapter';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import {Icon} from '@bonprix/design-system';
import {Constants} from '../view-constants.js';
import {useBreakpointUp} from 'bonprix-design-system';
import {sendClickedFiltersEvent, sendViewedFiltersEvent} from '@shopping-funnel/scripts/helper/algolia-tracking';
import {getAlgoliaFilterStrings} from '@shopping-funnel/scripts/helper/algolia-filter-context.ts';
import {categoryIcon} from './category-icons.ts';

// Category icon in front of the slide text; ml-4 keeps the outer inset at the slide's usual 16px.
const SLIDE_ICON = 'size-4 self-center ml-4';

export default function Subshops({
    identifier,
    subshops,
    links,
    trackingId,
    rootShopName,
    filterSubmit,
    subshopsTopOffset,
    hasActiveFilters,
}) {
    const {shopId, rootCategoryPath, currentSearchParams} = useContext(articleOverviewStaticContext);
    const isXLargeViewportSticky = useBreakpointUp('xl');

    const subshopsRef = useRef(null);
    const sliderRef = useRef(null);
    const hasSentViewedFiltersEvent = useRef(false);
    const [showPrev, setShowPrev] = useState(false);
    const [showNext, setShowNext] = useState(false);

    useEffect(() => {
        if (!subshopsRef.current) return;
        const top = isXLargeViewportSticky ? subshopsTopOffset - 1 : '';
        subshopsRef.current.style.top = top ? `${top}px` : '';
    }, [subshopsTopOffset, isXLargeViewportSticky]);

    useEffect(() => {
        scrollSelectedSubshopToCenter();
        updateButtonVisibility();
    }, [subshops]);

    useEffect(() => {
        if (!subshopsRef.current || hasSentViewedFiltersEvent.current || !subshops?.length) {
            return;
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting || hasSentViewedFiltersEvent.current) {
                return;
            }

            const visibleFilters = getAlgoliaFilterStrings(
                identifier,
                subshops
                    .filter((subshop) => subshop.numberOfItems > 0)
                    .map((subshop) => subshop.value),
            );

            if (!visibleFilters.length) {
                return;
            }

            hasSentViewedFiltersEvent.current = true;
            sendViewedFiltersEvent(visibleFilters);
            observer.disconnect();
        }, {
            threshold: 0.25,
        });

        observer.observe(subshopsRef.current);

        return () => observer.disconnect();
    }, [identifier, subshops]);

    const updateButtonVisibility = () => {
        const slider = sliderRef.current;
        if (slider) {
            setShowPrev(slider.scrollLeft > 0);
            setShowNext(slider.scrollLeft + slider.clientWidth < slider.scrollWidth - 1);
        } else {
            setShowPrev(false);
            setShowNext(false);
        }
    };

    const handleNextClick = () => {
        const slider = sliderRef.current;
        if (slider) {
            slider.scrollBy({left: slider.clientWidth, behavior: 'smooth'});
        }
    };

    const handlePrevClick = () => {
        const slider = sliderRef.current;
        if (slider) {
            const target = Math.max(0, slider.scrollLeft - slider.clientWidth);
            slider.scrollTo({left: target, behavior: 'smooth'});
        }
    };

    const scrollSelectedSubshopToCenter = () => {
        const slider = sliderRef.current;
        if (slider) {
            const selectedSubshop = slider.querySelector('.subshops__slide-active');
            if (selectedSubshop) {
                const sliderWidth = slider.clientWidth;
                const subshopWidth = selectedSubshop.clientWidth;
                const subshopOffsetLeft = selectedSubshop.offsetLeft;
                const scrollPosition = subshopOffsetLeft - sliderWidth / 2 + subshopWidth / 2;
                slider.scrollTo({left: scrollPosition, behavior: 'smooth'});
            }
        }
    };

    const slides = [];
    const marginClass = !hasActiveFilters ? ' mb-2 l:mb-6' : '';

    if (subshops) {
        slides.push(renderRootShopItem());
    }
    subshops?.map((subshop) => {
        slides.push(renderSubshopItem(subshop));
    });
    links?.map((link) => {
        slides.push(renderShopLink(link));
    });

    const selectSubshop = useCallback(
        (subshop, disabled) => {
            if (!disabled && !subshop.selected) {
                sendClickedFiltersEvent(getAlgoliaFilterStrings(identifier, [subshop.value]));
            }
            const filterData = new Map();
            filterData.set(identifier, {trackingId, clickedFilterOptions: [subshop.value], path: subshop.categoryPath});
            filterSubmit(filterData, undefined, disabled, true);
        },
        [identifier, trackingId, filterSubmit],
    );

    const selectRootShop = useCallback(() => {
        const filterData = new Map();
        filterData.set(identifier, {trackingId, clickedFilterOptions: [], path: rootCategoryPath.current});
        filterSubmit(filterData, undefined, false, true);
    }, [identifier, trackingId, filterSubmit]);

    return (
        <section className="article-overview__subshops" ref={subshopsRef}>
            {showPrev && (
                <button
                    className="subshops__slider-btn bg-clr-on-primary-high -prev"
                    onClick={handlePrevClick}
                    aria-label={Constants.ARIA_LABEL_SUBSHOPS_PREV}
                >
                    <Icon variant={'arrow-left'} />
                </button>
            )}
            <div className="subshops__slider pt-2 l:pt-0" ref={sliderRef} onScroll={updateButtonVisibility}>
                {slides}
            </div>
            {showNext && (
                <button
                    className="subshops__slider-btn bg-clr-on-primary-high -next"
                    onClick={handleNextClick}
                    aria-label={Constants.ARIA_LABEL_SUBSHOPS_NEXT}
                >
                    <Icon variant={'arrow-right'} />
                </button>
            )}
        </section>
    );

    function renderRootShopItem() {
        const selected = !subshops?.some((subshop) => subshop.selected);

        return (
            <button
                className={`subshops__slide text-clr-on-bg-high border border-solid border-clr-bg-main-light${marginClass}${selected ? ' subshops__slide-active' : ''}`}
                onClick={() => selectRootShop()}
                key={'subshop-root'}
            >
                <span
                    className={`subshops__slide-text l:hover:underline l:hover:decoration-solid l:hover:decoration-auto l:hover:underline-offset-[30%] l:hover:decoration-skip-ink-none${selected ? ' underline decoration-solid decoration-[15%] underline-offset-[30%] decoration-skip-ink-none' : ''}`}
                >
                    <span className="subshops__slide-focus-wrapper">Alle {rootShopName}</span>
                </span>
            </button>
        );
    }

    function renderSubshopItem(subshop) {
        const disabled = subshop.numberOfItems === 0;
        const icon = categoryIcon(subshop.value);

        const handleOnClick = (event) => {
            event.preventDefault();
            selectSubshop(subshop, disabled);
        };

        return (
            <a
                className={`subshops__slide border-y border-r border-l-0 border-solid border-clr-bg-main-light${marginClass}${disabled ? ' text-clr-on-bg-low' : ' text-clr-on-bg-high'}`}
                onClick={handleOnClick}
                href={subshop.link}
                key={'subshop-' + subshop.value}
            >
                {icon && <Icon variant={icon} className={SLIDE_ICON} />}
                <span
                    className={`subshops__slide-text${subshop.selected ? ' underline decoration-solid decoration-[15%] underline-offset-[30%] decoration-skip-ink-none subshops__slide-active' : ''} l:hover:underline l:hover:decoration-solid l:hover:decoration-auto l:hover:underline-offset-[30%] l:hover:decoration-skip-ink-none`}
                >
                    <span className={`subshops__slide-focus-wrapper${icon ? ' -with-icon' : ''}`}>{subshop.name}</span>
                </span>
            </a>
        );
    }

    function renderShopLink(link) {
        const icon = categoryIcon(link.id);

        const handleOnClick = (event) => {
            event.preventDefault();
            navigateToShopLandmark(link.id);
            try {
                const params = new URLSearchParams(currentSearchParams?.current?.toString() || '');
                params.delete(Constants.SEARCH_PARAMS_PAGE);
                params.delete(Constants.SEARCH_PARAMS_ANCHOR);
                params.delete(Constants.SEARCH_PARAMS_SCROLL_Y);

                const paramsString = params.toString();
                const separator = paramsString && link.path.includes('?') ? '&' : (paramsString ? '?' : '');
                const target = paramsString ? `${link.path}${separator}${paramsString}` : link.path;
                window.location.assign(target);
            } catch (err) {
                window.location.assign(link.path);
            }
        };

        return (
            <a
                className={`subshops__slide border-y border-r border-l-0 first:border-l border-solid border-clr-bg-main-light text-clr-on-bg-high${marginClass}${link.selected ? ' subshops__slide-active' : ''}`}
                onClick={handleOnClick}
                href={link.path}
                key={'link-' + link.id}
            >
                {icon && <Icon variant={icon} className={SLIDE_ICON} />}
                <span className="subshops__slide-text l:hover:underline l:hover:decoration-solid l:hover:decoration-auto l:hover:underline-offset-[30%] l:hover:decoration-skip-ink-none">
                    <span className={`subshops__slide-focus-wrapper${icon ? ' -with-icon' : ''}`}>{link.name}</span>
                </span>
            </a>
        );
    }

    function navigateToShopLandmark(redirectDestination) {
        const redirectOrigin = shopId.current ? shopId.current : 0;
        let landmarkData = {
            landmark: 'userinterface',
            redirect_origin: `${redirectOrigin}`,
            redirect_destination: `${redirectDestination}`,
        };
        Landmark.sendLandmark(landmarkData);
    }
}
